import React, { useRef, useState } from "react";
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from "@material-ui/core";
import MoreHoriz from "@material-ui/icons/MoreHoriz";
import LinkIcon from "@material-ui/icons/Link";
import GetApp from "@material-ui/icons/GetApp";
import MailOutline from "@material-ui/icons/MailOutline";
import SmsOutlined from "@material-ui/icons/SmsOutlined";
import Refresh from "@material-ui/icons/Refresh";
import Replay from "@material-ui/icons/Replay";
import Block from "@material-ui/icons/Block";
import Undo from "@material-ui/icons/Undo";
import ReceiptOutlined from "@material-ui/icons/ReceiptOutlined";
import Box from "Components/Atoms/Box";
import Button from "Components/Atoms/Button";
import Typography from "Components/Atoms/Typography";
import { useTranslation } from "react-i18next";
import server from "utils/server";
import {
  copyPaymentRequestLink,
  newRequestKey,
  PaymentRequest,
  PaymentRequestError,
  reconcilePaymentRequest,
  resolvePaymentRequestClarifications,
  retryRefundNote,
  revokePaymentRequest,
  sendPaymentRequest,
} from "api/paymentRequestsApi";
import RefundModal from "./RefundModal";
import RenewModal from "./RenewModal";
import CreditModal from "./CreditModal";
import { dateTime, euro, percent } from "./format";
import { useFieldIds } from "./fieldIds";

/** Async Clipboard API with items (newer than this TypeScript's DOM types). */
type ClipboardItemWindow = {
  ClipboardItem?: new (items: Record<string, Promise<Blob>>) => unknown;
};
type ClipboardWithItems = { write?: (items: unknown[]) => Promise<void> };

export type PaymentRequestRights = { manage: boolean; refund: boolean };

type PaymentAction = {
  label: string;
  icon: React.ReactNode;
  detail?: string;
  disabled?: boolean;
  danger?: boolean;
  onSelect: () => void;
};

const STATUS_LABEL: Record<PaymentRequest["status"], string> = {
  open: "Payment status open",
  processing: "Payment in progress",
  expired: "Payment status expired",
  paid: "Paid",
  partially_refunded: "Partially refunded",
  refunded: "Refunded",
  revoked: "Payment status revoked",
};

const CLARIFICATION_LABEL: Record<string, string> = {
  reservation_cancelled: "Reservation cancelled – please clarify",
  late_payment: "Payment arrived after expiry or revocation",
  duplicate_payment: "Paid a second time",
  amount_mismatch: "Paid amount differs",
  provider_unclear: "Mollie outcome unclear – reconcile",
};

const DELIVERY_LABEL: Record<string, string> = {
  queued: "Queued",
  sending: "Sending",
  sent: "Sent",
  failed: "Not delivered",
  unknown: "Delivery unclear",
};

const row = {
  display: "flex",
  justifyContent: "space-between",
  gap: 8,
  flexWrap: "wrap",
} as const;

const PaymentRequestCard: React.FC<{
  restaurantId: string;
  request: PaymentRequest;
  rights: PaymentRequestRights;
  onChange: (request: PaymentRequest) => void;
}> = ({ restaurantId, request, rights, onChange }) => {
  const { t } = useTranslation("reservations");
  const fieldId = useFieldIds("payment-request-card");
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [modal, setModal] = useState<"refund" | "renew" | "credit" | null>(
    null
  );
  const dialogAfterMenu = useRef<"refund" | "renew" | "credit" | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
  const menuId = fieldId("actions");
  const triggerId = fieldId("actions-trigger");
  const [clarificationNote, setClarificationNote] = useState("");

  const run = async (
    work: () => Promise<PaymentRequest | void>,
    success?: string
  ) => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const updated = await work();
      if (updated) onChange(updated);
      if (success) setNotice(t(success));
    } catch (err: any) {
      setError(err.message);
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  const send = (channel: "email" | "sms") =>
    run(async () => {
      const deliveryKey = newRequestKey();
      try {
        return await sendPaymentRequest(restaurantId, request.id, {
          channel,
          deliveryKey,
        });
      } catch (err) {
        if (
          !(err instanceof PaymentRequestError) ||
          err.code !== "delivery_unknown"
        )
          throw err;
        if (
          !window.confirm(t("The last delivery is unclear. Send again anyway?"))
        )
          return;
        return sendPaymentRequest(restaurantId, request.id, {
          channel,
          deliveryKey,
          acknowledgeUnknown: true,
        });
      }
    });

  const copyLink = () => {
    if (running.current) return;
    const link = copyPaymentRequestLink(restaurantId, request.id);
    // Safari writes to the clipboard only during the click, so the write
    // starts now and receives the link once it has loaded.
    const { ClipboardItem } = window as unknown as ClipboardItemWindow;
    const clipboard = navigator.clipboard as Clipboard & ClipboardWithItems;
    const copied =
      ClipboardItem && clipboard?.write
        ? clipboard.write([
            new ClipboardItem({
              "text/plain": link.then(
                ({ url }) => new Blob([url], { type: "text/plain" })
              ),
            }),
          ])
        : link.then(({ url }) => navigator.clipboard.writeText(url));
    const copiedOrNot = copied.then(
      () => true,
      () => false
    );
    run(async () => {
      const { url } = await link;
      if (!(await copiedOrNot)) window.prompt(t("Copy the payment link"), url);
    }, "Link copied");
  };

  const revoke = () => {
    if (
      !window.confirm(
        t("Revoke this payment link? An open checkout is cancelled.")
      )
    )
      return;
    run(() => revokePaymentRequest(restaurantId, request.id));
  };

  const downloadReceipt = (paymentId: string) =>
    run(async () => {
      // Staff route of the request: a signed-in team member, checked by the API.
      const { data } = await server.get(
        `/v03/paymentRequests/${restaurantId}/${request.id}/receipts/${paymentId}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `beleg-${paymentId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });

  const openClarifications = request.clarifications.filter(
    (entry) => !entry.resolvedAt
  );
  const paidAttempts = request.attempts.filter(
    (attempt) => attempt.status === "paid"
  );
  const lastDeliveries = request.deliveries.slice(-3);

  const latestDelivery = request.deliveries[request.deliveries.length - 1];
  const deliveryProblem =
    latestDelivery && ["failed", "unknown"].includes(latestDelivery.status);
  const primaryReceipt =
    paidAttempts.length === 1 ? paidAttempts[0].molliePaymentId : null;

  const wasSent = request.deliveries.some(
    (delivery) => delivery.status === "sent" && delivery.channel !== "copy"
  );
  const actionGroups: PaymentAction[][] = [
    rights.manage && request.actions.send
      ? [
          {
            label: t(wasSent ? "Send again by email" : "Send by email"),
            icon: <MailOutline fontSize="small" />,
            detail: request.payer.email || t("No email available"),
            disabled: !request.payer.email,
            onSelect: () => send("email"),
          },
          {
            label: t(wasSent ? "Send again by SMS" : "Send by SMS"),
            icon: <SmsOutlined fontSize="small" />,
            detail: request.payer.phone || t("No phone available"),
            disabled: !request.payer.phone,
            onSelect: () => send("sms"),
          },
        ]
      : [],
    [
      {
        label: t("Check status"),
        icon: <Refresh fontSize="small" />,
        onSelect: () =>
          run(() => reconcilePaymentRequest(restaurantId, request.id)),
      },
      ...(rights.manage && request.actions.renew
        ? [
            {
              label: t("Request again"),
              icon: <Replay fontSize="small" />,
              detail: t("Replaces the previous link"),
              onSelect: () => {
                dialogAfterMenu.current = "renew";
              },
            },
          ]
        : []),
      ...(rights.manage && request.actions.revoke
        ? [
            {
              label: t("Revoke"),
              icon: <Block fontSize="small" />,
              danger: true,
              onSelect: revoke,
            },
          ]
        : []),
    ],
    [
      ...(rights.manage && request.actions.credit
        ? [
            {
              label: t("Confirm credit"),
              icon: <ReceiptOutlined fontSize="small" />,
              detail: t("Record the deduction on the final bill"),
              onSelect: () => {
                dialogAfterMenu.current = "credit";
              },
            },
          ]
        : []),
      ...(rights.refund && request.actions.refund
        ? [
            {
              label: t("Refund"),
              icon: <Undo fontSize="small" />,
              detail: t("Choose amount and positions"),
              onSelect: () => {
                dialogAfterMenu.current = "refund";
              },
            },
          ]
        : []),
      ...paidAttempts
        .filter(() => paidAttempts.length > 1)
        .flatMap(({ molliePaymentId }) =>
          molliePaymentId
            ? [
                {
                  label: t("Download receipt"),
                  icon: <GetApp fontSize="small" />,
                  detail: molliePaymentId,
                  onSelect: () => downloadReceipt(molliePaymentId),
                },
              ]
            : []
        ),
    ],
  ];
  const menuItems: React.ReactNode[] = [];
  actionGroups.forEach((group, groupIndex) => {
    if (!group.length) return;
    const beginsGroup = menuItems.length > 0;
    group.forEach((item, index) => {
      menuItems.push(
        <MenuItem
          key={`${groupIndex}-${index}`}
          disabled={busy || item.disabled}
          className={[
            beginsGroup && index === 0
              ? "payment-request-menu__group-start"
              : "",
            item.danger ? "payment-request-menu__danger" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => {
            setMenuAnchor(null);
            item.onSelect();
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} secondary={item.detail} />
        </MenuItem>
      );
    });
  });

  return (
    <Box
      className="payment-request-card"
      data-testid="payment-request"
      aria-busy={busy}
    >
      <div className="payment-request-card__heading">
        <strong>{request.purpose}</strong>
        <strong className="payment-request-card__amount">
          {euro(request.items.totalCents)}
        </strong>
      </div>
      <div className="payment-request-card__status-row">
        <span
          className={`payment-request-status payment-request-status--${request.status}`}
        >
          {t(STATUS_LABEL[request.status])}
        </span>
        <span className="payment-request-card__kind">
          {request.kind === "deposit" ? t("Deposit") : t("Payment")}
          {request.testmode && (
            <span className="payment-request-card__test">
              {" "}
              · {t("Test mode")}
            </span>
          )}
        </span>
      </div>
      <div className="payment-request-card__context">
        <p className="payment-request-card__payer">
          {request.payer.name}
          {request.invoiceRecipient?.company &&
            ` · ${request.invoiceRecipient.company}`}
        </p>
        <p className="payment-request-card__meta">
          {request.paidAt
            ? dateTime(request.paidAt)
            : `${t("Valid until")} ${dateTime(request.expiresAt)}`}
          {request.refundedCents > 0 &&
            ` · ${t("Refunded")}: ${euro(request.refundedCents)}`}
          {request.pendingRefundCents > 0 &&
            ` · ${t("Refund pending")}: ${euro(request.pendingRefundCents)}`}
        </p>
      </div>
      {request.credit && (
        <p
          className={
            !request.credit.posReference ||
            (request.credit.amountCents ?? 0) > request.creditableCents
              ? "payment-request-card__warning"
              : "payment-request-card__meta"
          }
          role="status"
        >
          {!request.credit.posReference
            ? t("Old marker without POS receipt (unverified)")
            : (request.credit.amountCents ?? 0) > request.creditableCents
            ? t("Marked as credited before the refund – check the final bill")
            : t(
                "Manually confirmed: {{amount}} credited on final bill {{reference}}",
                {
                  amount: euro(request.credit.amountCents ?? request.paidCents),
                  reference: request.credit.posReference,
                }
              )}
        </p>
      )}
      {deliveryProblem && (
        <p className="payment-request-card__warning" role="status">
          {latestDelivery.kind === "confirmation"
            ? t("Payment confirmation")
            : t("Payment link")}
          : {t(DELIVERY_LABEL[latestDelivery.status])}
        </p>
      )}
      {request.overpaidCents > 0 && (
        <Typography variant="text-3" color="error" translation={null}>
          <span role="status">
            ⚠ {t("Paid more than requested")}: {euro(request.overpaidCents)} –{" "}
            {t("please clarify or refund the additional payment")}
          </span>
        </Typography>
      )}

      {openClarifications.map((entry, index) => (
        <Typography
          key={index}
          variant="text-3"
          color="error"
          translation={null}
        >
          <span role="status">
            ⚠ {t(CLARIFICATION_LABEL[entry.reason] ?? entry.reason)}
          </span>
        </Typography>
      ))}
      {rights.refund && openClarifications.length > 0 && (
        <Box style={{ display: "flex", gap: 8 }}>
          <TextField
            id={fieldId("clarification-note")}
            size="small"
            variant="outlined"
            label={t("Clarification note")}
            value={clarificationNote}
            onChange={(event) => setClarificationNote(event.target.value)}
            style={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            translation={null}
            disabled={busy || !clarificationNote.trim()}
            onClick={() =>
              run(() =>
                resolvePaymentRequestClarifications(
                  restaurantId,
                  request.id,
                  clarificationNote.trim()
                )
              )
            }
          >
            {t("Mark as clarified")}
          </Button>
        </Box>
      )}

      {error && (
        <Typography color="error" block translation={null}>
          <span role="alert">{error}</span>
        </Typography>
      )}
      {notice && (
        <p
          className="payment-request-card__notice"
          role="status"
          aria-live="polite"
        >
          {notice}
        </p>
      )}

      <div className="payment-request-card__tools">
        {rights.manage && request.actions.send && (
          <Button
            variant="outlined"
            translation={null}
            disabled={busy}
            loading={busy}
            onClick={copyLink}
            startIcon={(props) => <LinkIcon {...props} />}
          >
            {t("Copy link")}
          </Button>
        )}
        {primaryReceipt && (
          <Button
            variant="outlined"
            translation={null}
            aria-label={t("Download receipt")}
            disabled={busy}
            loading={busy}
            onClick={() => downloadReceipt(primaryReceipt)}
            startIcon={(props) => <GetApp {...props} />}
          >
            {t("Receipt short")}
          </Button>
        )}
        <Button
          variant="transparent"
          translation={null}
          id={triggerId}
          className="payment-request-card__more-button"
          aria-label={t("More actions")}
          aria-haspopup="menu"
          aria-controls={menuAnchor ? menuId : undefined}
          aria-expanded={Boolean(menuAnchor)}
          aria-disabled={busy}
          onClick={(event) => {
            if (!busy) setMenuAnchor(event.currentTarget);
          }}
          endIcon={(props) => <MoreHoriz {...props} />}
        >
          {t("Actions short")}
        </Button>
      </div>
      <Menu
        variant="menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        TransitionProps={{
          onExited: () => {
            if (dialogAfterMenu.current) {
              const next = dialogAfterMenu.current;
              dialogAfterMenu.current = null;
              setModal(next);
            }
          },
        }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "payment-request-menu", elevation: 0 }}
        MenuListProps={{ id: menuId, "aria-labelledby": triggerId }}
      >
        {menuItems}
      </Menu>
      <details className="payment-request-card__details">
        <summary>{t("Details and history")}</summary>
        <div className="payment-request-card__history">
          {request.expiryShortenedByReservation && (
            <Typography variant="text-3" translation={null}>
              {t(
                "The validity was shortened to the new end of the reservation."
              )}
            </Typography>
          )}
          <Typography variant="text-3" translation={null}>
            {t("Payer")}: {request.payer.name}
            {!request.payerIsReservationGuest
              ? ` (${t("not the reservation guest")})`
              : ""}
            {request.payer.email ? ` · ${request.payer.email}` : ""}
            {request.payer.phone ? ` · ${request.payer.phone}` : ""}
          </Typography>

          {request.items.lines.length > 1 &&
            request.items.lines.map((line) => (
              <Box key={line.index} style={row}>
                <Typography variant="text-3" translation={null}>
                  {line.quantity} × {line.description} ({percent(line.vatRate)})
                </Typography>
                <Typography variant="text-3" translation={null}>
                  {euro(line.totalCents)}
                </Typography>
              </Box>
            ))}
          {request.items.vatGroups.map((group) => (
            <Typography
              key={group.vatRate}
              variant="text-3"
              color="subdued"
              translation={null}
            >
              {t("incl. VAT")} {percent(group.vatRate)}: {euro(group.vatCents)}
            </Typography>
          ))}

          {request.paidCents > 0 && (
            <Typography variant="text-3" translation={null}>
              {t("Paid")}: {euro(request.paidCents)} ·{" "}
              {dateTime(request.paidAt)}
              {request.refundedCents > 0
                ? ` · ${t("Refunded")}: ${euro(request.refundedCents)}`
                : ""}
              {request.pendingRefundCents > 0
                ? ` · ${t("Refund pending")}: ${euro(
                    request.pendingRefundCents
                  )}`
                : ""}
            </Typography>
          )}

          {lastDeliveries.map((delivery) => (
            <Typography
              key={delivery.id}
              variant="text-3"
              color={
                delivery.status === "failed" || delivery.status === "unknown"
                  ? "error"
                  : "subdued"
              }
              translation={null}
            >
              {delivery.channel === "copy"
                ? t("Link copied")
                : `${
                    delivery.kind === "confirmation"
                      ? `${t("Payment confirmation")} · `
                      : ""
                  }${delivery.channel === "email" ? t("Email") : "SMS"} → ${
                    delivery.to
                  }`}
              {" · "}
              {t(DELIVERY_LABEL[delivery.status])} ·{" "}
              {dateTime(delivery.sentAt ?? delivery.createdAt)}
              {delivery.error ? ` · ${delivery.error}` : ""}
            </Typography>
          ))}

          {request.refunds.map((refund) => (
            <Box key={refund.id} style={row}>
              <Typography variant="text-3" translation={null}>
                {t("Refund")} {euro(refund.amountCents)} · {refund.status}
                {refund.note.number
                  ? ` · ${t("Cancellation invoice")} ${refund.note.number}`
                  : ""}
                {refund.error ? ` · ${refund.error}` : ""}
                {refund.note.status === "failed"
                  ? ` · ${refund.note.error}`
                  : ""}
              </Typography>
              {rights.refund &&
                refund.status === "refunded" &&
                refund.note.status !== "created" && (
                  <Button
                    variant="outlined"
                    translation={null}
                    disabled={busy}
                    onClick={() =>
                      run(() =>
                        retryRefundNote(restaurantId, request.id, refund.id)
                      )
                    }
                  >
                    {t("Create cancellation invoice")}
                  </Button>
                )}
            </Box>
          ))}
        </div>
      </details>

      {modal === "refund" && (
        <RefundModal
          restaurantId={restaurantId}
          request={request}
          onClose={() => setModal(null)}
          onDone={(updated) => {
            setModal(null);
            onChange(updated);
          }}
        />
      )}
      {modal === "credit" && (
        <CreditModal
          restaurantId={restaurantId}
          request={request}
          onClose={() => setModal(null)}
          onDone={(updated) => {
            setModal(null);
            onChange(updated);
          }}
        />
      )}
      {modal === "renew" && (
        <RenewModal
          restaurantId={restaurantId}
          request={request}
          onClose={() => setModal(null)}
          onDone={(updated) => {
            setModal(null);
            onChange(updated);
          }}
        />
      )}
    </Box>
  );
};

export default PaymentRequestCard;
